import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { API_BASE } from '../../config/api';
import { getTitlePlainText } from '../../utils/titleFormat';

export const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [blogs, filterCategory, filterFeatured, filterPublished]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/blog_posts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch blogs');

      const data = await response.json();
      setBlogs(data.blog_posts || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...blogs];

    if (filterCategory !== 'all') {
      filtered = filtered.filter(blog => blog.category === filterCategory);
    }

    if (filterFeatured !== 'all') {
      const isFeatured = filterFeatured === 'featured';
      filtered = filtered.filter(blog => blog.featured_article === isFeatured);
    }

    if (filterPublished !== 'all') {
      const isPublished = filterPublished === 'published';
      filtered = filtered.filter(blog => blog.published === isPublished);
    }

    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredBlogs(filtered);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/blog_posts/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete');

      setBlogs(prev => prev.filter(blog => blog.id !== id));
    } catch (err) {
      alert('Failed to delete blog post');
    }
  };

  const togglePublish = async (id, currentStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/blog_posts/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to update');

      setBlogs(prev => 
        prev.map(blog => blog.id === id ? { ...blog, published: !currentStatus } : blog)
      );
    } catch (err) {
      alert('Failed to update publish status');
    }
  };

  const getUniqueCategories = () => {
    const categories = [...new Set(blogs.map(blog => blog.category))];
    return categories.filter(c => c);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading blog posts...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#111827] mb-2">Blog Posts</h1>
            <p className="text-[#6B7280]">Manage blog content with SEO</p>
          </div>
          <button
            onClick={() => navigate('/admin/blogs/new')}
            className="px-6 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all flex items-center gap-2"
            data-testid="add-blog-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Blog Post
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Category</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Categories</option>
                {getUniqueCategories().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Featured</label>
              <select
                value={filterFeatured}
                onChange={(e) => setFilterFeatured(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Posts</option>
                <option value="featured">Featured Only</option>
                <option value="not-featured">Not Featured</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111827] mb-2">Status</label>
              <select
                value={filterPublished}
                onChange={(e) => setFilterPublished(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <p className="text-sm text-[#6B7280]">
              Showing {filteredBlogs.length} of {blogs.length} blog posts
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-[#E5E7EB] rounded-xl overflow-hidden">
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-[#9CA3AF] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              <p className="text-[#6B7280]">No blog posts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC]">
                  <tr className="border-b-2 border-[#E5E7EB]">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Featured</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-[#6B7280]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlogs.map((blog) => (
                    <tr
                      key={blog.id}
                      className="border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors"
                    >
                      <td className="py-3 px-4 text-sm font-medium text-[#111827]">{getTitlePlainText(blog.title)}</td>
                      <td className="py-3 px-4 text-sm text-[#6B7280]">{blog.category}</td>
                      <td className="py-3 px-4">
                        {blog.featured_article && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">⭐ Featured</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => togglePublish(blog.id, blog.published)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            blog.published
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {blog.published ? 'Published' : 'Draft'}
                        </button>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/admin/blogs/${blog.id}/edit`)}
                            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(blog.id)}
                            className="text-red-600 hover:text-red-800 font-semibold text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default BlogList;